/**
 * The seam through which the interactive `/signin` surface obtains a Firebase ID
 * token, without any module here importing the `firebase` SDK. The concrete
 * adapter — which imports `firebase/auth`, runs the email/password + Google
 * popup flows, and calls `getIdToken()` — lives in `firebaseAuthAdapter.ts` and
 * is registered at boot in `main.ts` via `setFirebaseAuthProvider`. Firebase
 * sign-in is built in, so the adapter registers unconditionally and its
 * *presence* is what makes the interactive `/signin` form render; the server
 * mirror is the `verifyFirebaseToken` verifier that gates
 * `POST /api/auth/session`.
 */
export interface FirebaseAuthProvider {
  /**
   * Run the Google popup sign-in and resolve to a Firebase ID token, or `null`
   * when the user aborts (closes/cancels the popup) — an aborted sign-in is not
   * an error. Rejects with an `Error` (a user-facing message) on real failure.
   */
  signInWithGoogle(): Promise<string | null>;
  /**
   * Sign in with an email/password and resolve to a Firebase ID token, or `null`
   * if no token was produced. Rejects with an `Error` (a user-facing message)
   * on a bad credential or other failure.
   */
  signInWithEmail(email: string, password: string): Promise<string | null>;
  /**
   * Clear the Firebase client session established by a sign-in this session, so
   * an explicit sign-out leaves no persisted (auto-refreshing) Firebase identity
   * behind. A no-op — and, importantly, does not load the SDK — when no Firebase
   * sign-in happened this session, keeping Firebase off the common logout path.
   */
  signOut(): Promise<void>;
}

let provider: FirebaseAuthProvider | null = null;

/** Register (or, with `null`, clear) the concrete Firebase auth adapter. */
export function setFirebaseAuthProvider(next: FirebaseAuthProvider | null): void {
  provider = next;
}

/** The registered Firebase auth adapter, or `null` when none is available. */
export function getFirebaseAuthProvider(): FirebaseAuthProvider | null {
  return provider;
}
